import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Activity, Cpu, Eye, Banknote } from "lucide-react";
import api from "@/lib/api";

export default function TrustSection() {
  const [stats, setStats] = useState({ brands: 48, countries: 4, payout_methods: 12, transactions: 0 });

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Honest, verifiable facts only. No inflated counters or fake social proof.
  const facts = [
    { value: stats.brands, suffix: "", label: "Supported brands", color: "#00C853" },
    { value: stats.countries, suffix: "", label: "Countries served", color: "#0B3C5D" },
    { value: stats.payout_methods, suffix: "", label: "Payout methods", color: "#00C853" },
    { value: 2, suffix: "%", label: "Flat processing fee", color: "#0B3C5D" },
  ];

  const features = [
    { Icon: ShieldCheck, title: "Reviewed Submissions", desc: "Every transaction is reviewed by our operations team before payout." },
    { Icon: Lock, title: "256-bit Encryption", desc: "Bank-grade SSL & end-to-end encryption on all card data." },
    { Icon: Activity, title: "Transparent Status", desc: "Watch your transaction move from verifying to paid in your dashboard." },
    { Icon: Cpu, title: "Risk Checks", desc: "Automated checks flag duplicate cards and high-risk submissions." },
    { Icon: Eye, title: "Privacy First", desc: "Card numbers are masked in our database — never exposed after verification." },
    { Icon: Banknote, title: "Locked Rate", desc: "Your quoted rate is locked for 5 minutes — what you see is what you get." },
  ];

  return (
    <section className="py-24 relative overflow-hidden bg-[#0B3C5D] text-white" data-testid="trust-section">
      <div className="absolute inset-0 noise opacity-30 pointer-events-none" />
      <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#00C853]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-[#00C853]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00C853] font-semibold">How we keep you safe</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 tracking-tight">
            Security and transparency, by design.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {facts.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur"
              data-testid={`fact-${i}`}
            >
              <p className="font-display text-3xl sm:text-4xl font-bold" style={{ color: m.color }}>
                {m.value}{m.suffix}
              </p>
              <p className="mt-2 text-sm text-white/70">{m.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map(({ Icon, title, desc }, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur hover:bg-white/10 transition"
              data-testid={`trust-feature-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#00C853]/15 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#00C853]" strokeWidth={2.2} />
              </div>
              <h3 className="font-display text-lg font-bold mt-4">{title}</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
